import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { siteConfig } from "../../../config/site.config";
import { useExperience } from "../../../context/ExperienceContext";
import { getContactWhatsAppLink } from "../../../config/contact.config";
import { Icons } from "../../../icons/icons";
import "./Header.css";

const DEFAULT_DOCUMENT_TITLE = "Clínica Dionara Krause | Estética e Nutrição";

const HEADER_SCROLL_DELAY = 90;
const HEADER_SCROLL_EXTRA_OFFSET = 2;
const ROUTE_SCROLL_DELAY = 280;

const MIN_SCROLL_DURATION = 760;
const MAX_SCROLL_DURATION = 1350;

function getSectionNavigation(mode = "all") {
  const normalizedMode = String(mode || "all").toLowerCase();

  const servicesLabel =
    normalizedMode === "nutrition"
      ? "Acompanhamentos"
      : normalizedMode === "aesthetics"
        ? "Procedimentos"
        : "Serviços";

  const servicesTitle =
    normalizedMode === "nutrition"
      ? "Acompanhamentos | Clínica Dionara Krause"
      : normalizedMode === "aesthetics"
        ? "Procedimentos | Clínica Dionara Krause"
        : "Serviços | Clínica Dionara Krause";

  return [
    {
      label: "Início",
      target: "top",
      title: "Início | Clínica Dionara Krause",
    },
    {
      label: "Sobre",
      target: ".about-preview",
      title: "Sobre a Dionara | Clínica Dionara Krause",
    },
    {
      label: servicesLabel,
      target: ".procedures-section",
      title: servicesTitle,
    },
    {
      label: "Contato",
      target: ".contact-section",
      title: "Contato | Clínica Dionara Krause",
    },
  ];
}

function getHeaderOffset() {
  const header = document.querySelector(".header");
  return header?.offsetHeight || 96;
}

function getSectionElement(target) {
  if (target === "top") {
    return document.querySelector(
      ".page-hero-premium, .hero-section, .home-hero"
    );
  }

  return document.querySelector(target);
}

function getTargetScrollY(target) {
  if (target === "top") return 0;

  const section = getSectionElement(target);

  if (!section) return null;

  const headerOffset = getHeaderOffset();

  return (
    section.getBoundingClientRect().top +
    window.scrollY -
    headerOffset +
    HEADER_SCROLL_EXTRA_OFFSET
  );
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getScrollDuration(distance) {
  const absoluteDistance = Math.abs(distance);
  const dynamicDuration = 640 + absoluteDistance * 0.28;

  return clamp(dynamicDuration, MIN_SCROLL_DURATION, MAX_SCROLL_DURATION);
}

/*
  Efeito estável:
  - começa lento;
  - acelera no meio;
  - termina suave.
  Funciona subindo e descendo.
*/
function easeInOutQuint(progress) {
  if (progress < 0.5) {
    return 16 * progress * progress * progress * progress * progress;
  }

  return 1 - Math.pow(-2 * progress + 2, 5) / 2;
}

function getActiveSectionByScroll(sectionNavigation = []) {
  const headerOffset = getHeaderOffset();
  const activationPoint = headerOffset + window.innerHeight * 0.28;

  const topItem = sectionNavigation.find((item) => item.target === "top");
  const topSection = getSectionElement("top");

  if (topSection && topItem) {
    const rect = topSection.getBoundingClientRect();

    if (rect.top <= activationPoint && rect.bottom > activationPoint) {
      return topItem;
    }
  }

  for (const item of sectionNavigation) {
    if (item.target === "top") continue;

    const section = getSectionElement(item.target);

    if (!section) continue;

    const rect = section.getBoundingClientRect();

    if (rect.top <= activationPoint && rect.bottom > activationPoint) {
      return item;
    }
  }

  return null;
}

export default function Header() {
  const { mode } = useExperience();
  const navigate = useNavigate();
  const location = useLocation();

  const delayTimerRef = useRef(null);
  const routeTimerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const scrollJobIdRef = useRef(0);
  const isAnimatingScrollRef = useRef(false);
  const activeTargetRef = useRef(null);

  const originalScrollBehaviorRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingSection, setPendingSection] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  const { brand } = siteConfig;
  const whatsappHref = getContactWhatsAppLink(mode);

  const sectionNavigation = useMemo(() => getSectionNavigation(mode), [mode]);

  const navigationMap = useMemo(() => {
    return sectionNavigation.reduce((acc, item) => {
      acc[item.target] = item;
      return acc;
    }, {});
  }, [sectionNavigation]);

  const forceBodyUnlocked = () => {
    document.body.style.overflow = "";
  };

  const setDocumentTitleByTarget = (target) => {
    const selectedItem = navigationMap[target];
    document.title = selectedItem?.title || DEFAULT_DOCUMENT_TITLE;
  };

  const clearTimers = () => {
    if (delayTimerRef.current) {
      window.clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }

    if (routeTimerRef.current) {
      window.clearTimeout(routeTimerRef.current);
      routeTimerRef.current = null;
    }
  };

  const restoreScrollBehavior = () => {
    if (originalScrollBehaviorRef.current !== null) {
      document.documentElement.style.scrollBehavior =
        originalScrollBehaviorRef.current;

      originalScrollBehaviorRef.current = null;
    }
  };

  const stopScrollAnimation = () => {
    scrollJobIdRef.current += 1;

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    isAnimatingScrollRef.current = false;
    activeTargetRef.current = null;

    restoreScrollBehavior();
  };

  const cancelEverything = () => {
    clearTimers();
    stopScrollAnimation();
  };

  const animateScrollTo = (targetY, targetKey) => {
    stopScrollAnimation();

    const startY = window.scrollY || window.pageYOffset || 0;
    const finalY = Math.max(0, Math.round(targetY));
    const distance = finalY - startY;

    if (Math.abs(distance) < 3) {
      window.scrollTo({
        top: finalY,
        left: 0,
        behavior: "auto",
      });

      return;
    }

    const jobId = scrollJobIdRef.current + 1;
    scrollJobIdRef.current = jobId;

    const duration = getScrollDuration(distance);
    const startTime = performance.now();

    isAnimatingScrollRef.current = true;
    activeTargetRef.current = targetKey;

    if (originalScrollBehaviorRef.current === null) {
      originalScrollBehaviorRef.current =
        document.documentElement.style.scrollBehavior || "";
    }

    document.documentElement.style.scrollBehavior = "auto";

    const step = (currentTime) => {
      if (jobId !== scrollJobIdRef.current) return;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutQuint(progress);

      const nextY = Math.round(startY + distance * easedProgress);

      window.scrollTo({
        top: nextY,
        left: 0,
        behavior: "auto",
      });

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(step);
        return;
      }

      window.scrollTo({
        top: finalY,
        left: 0,
        behavior: "auto",
      });

      animationFrameRef.current = null;
      isAnimatingScrollRef.current = false;
      activeTargetRef.current = null;

      restoreScrollBehavior();

      const finalItem = navigationMap[targetKey];

      setActiveSection(finalItem?.target || null);
      document.title = finalItem?.title || DEFAULT_DOCUMENT_TITLE;
    };

    animationFrameRef.current = window.requestAnimationFrame(step);
  };

  const scrollToSection = (target) => {
    const targetY = getTargetScrollY(target);

    if (targetY === null) return;

    animateScrollTo(targetY, target);
  };

  const scheduleScrollToSection = (target, delay = HEADER_SCROLL_DELAY) => {
    cancelEverything();

    setActiveSection(target);
    setDocumentTitleByTarget(target);

    delayTimerRef.current = window.setTimeout(() => {
      scrollToSection(target);
      delayTimerRef.current = null;
    }, delay);
  };

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      return;
    }

    forceBodyUnlocked();
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) {
        setMenuOpen(false);
        forceBodyUnlocked();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!pendingSection) return;
    if (location.pathname !== "/") return;

    cancelEverything();

    routeTimerRef.current = window.setTimeout(() => {
      scheduleScrollToSection(pendingSection, HEADER_SCROLL_DELAY);
      setPendingSection(null);
      routeTimerRef.current = null;
    }, ROUTE_SCROLL_DELAY);

    return () => {
      if (routeTimerRef.current) {
        window.clearTimeout(routeTimerRef.current);
        routeTimerRef.current = null;
      }
    };
  }, [location.pathname, pendingSection]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      document.title = DEFAULT_DOCUMENT_TITLE;
      return;
    }

    let ticking = false;

    const updateActiveSection = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(() => {
        if (isAnimatingScrollRef.current && activeTargetRef.current) {
          const selectedItem = navigationMap[activeTargetRef.current];

          setActiveSection(selectedItem?.target || null);
          document.title = selectedItem?.title || DEFAULT_DOCUMENT_TITLE;

          ticking = false;
          return;
        }

        const activeItem = getActiveSectionByScroll(sectionNavigation);

        setActiveSection(activeItem?.target || null);
        document.title = activeItem?.title || DEFAULT_DOCUMENT_TITLE;

        ticking = false;
      });
    };

    updateActiveSection();

    window.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });

    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, [location.pathname, sectionNavigation, navigationMap]);

  useEffect(() => {
    const cancelOnUserInteraction = () => {
      if (!isAnimatingScrollRef.current) return;

      stopScrollAnimation();

      const activeItem = getActiveSectionByScroll(sectionNavigation);

      setActiveSection(activeItem?.target || null);
      document.title = activeItem?.title || DEFAULT_DOCUMENT_TITLE;
    };

    const cancelOnKeyboardNavigation = (event) => {
      const keysThatMovePage = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];

      if (!keysThatMovePage.includes(event.key)) return;

      cancelOnUserInteraction();
    };

    window.addEventListener("wheel", cancelOnUserInteraction, {
      passive: true,
    });

    window.addEventListener("touchstart", cancelOnUserInteraction, {
      passive: true,
    });

    window.addEventListener("keydown", cancelOnKeyboardNavigation);

    return () => {
      window.removeEventListener("wheel", cancelOnUserInteraction);
      window.removeEventListener("touchstart", cancelOnUserInteraction);
      window.removeEventListener("keydown", cancelOnKeyboardNavigation);
    };
  }, [sectionNavigation]);

  useEffect(() => {
    return () => {
      cancelEverything();
      forceBodyUnlocked();
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, []);

  const closeMenu = () => {
    setMenuOpen(false);
    forceBodyUnlocked();
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const nextValue = !prev;

      if (!nextValue) {
        forceBodyUnlocked();
      }

      return nextValue;
    });
  };

  const handleSectionNavigation = (target) => {
    closeMenu();

    setActiveSection(target);
    setDocumentTitleByTarget(target);

    if (location.pathname !== "/") {
      cancelEverything();

      setPendingSection(target);
      navigate("/");
      return;
    }

    scheduleScrollToSection(target, HEADER_SCROLL_DELAY);
  };

  const renderBrand = (className = "brand") => (
    <button
      type="button"
      className={className}
      aria-label={`${brand.name} Home`}
      onClick={() => handleSectionNavigation("top")}
    >
      <div className="brand-icon-wrapper">
        <img src={brand.logo} alt={brand.logoAlt} className="brand-icon" />
      </div>

      <div className="brand-text">
        <span className="brand-title">{brand.name}</span>
        <span className="brand-subtitle">{brand.subtitle}</span>
      </div>
    </button>
  );

  const renderNavigation = () =>
    sectionNavigation.map((item) => (
      <button
        key={item.target}
        type="button"
        className={`header__nav-link ${
          activeSection === item.target ? "is-active" : ""
        }`}
        onClick={() => handleSectionNavigation(item.target)}
      >
        {item.label}
      </button>
    ));

  return (
    <>
      <header className="header">
        <div className="container header-content">
          {renderBrand()}

          <nav className="nav nav-desktop" aria-label="Navegação principal">
            {renderNavigation()}
          </nav>

          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="header-whatsapp"
            aria-label="Agendar atendimento pelo WhatsApp"
          >
            <Icons.Calendar className="header-whatsapp__icon" />
            <span>Agendar</span>
          </a>

          <button
            type="button"
            className={`menu-toggle ${menuOpen ? "is-active" : ""}`}
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={menuOpen}
            onClick={toggleMenu}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <div
        className={`mobile-menu-overlay ${menuOpen ? "is-open" : ""}`}
        onClick={closeMenu}
      />

      <aside className={`mobile-menu ${menuOpen ? "is-open" : ""}`}>
        <div className="mobile-menu__header">
          <div className="mobile-menu__brand">
            <div className="brand-icon-wrapper">
              <img src={brand.logo} alt={brand.logoAlt} className="brand-icon" />
            </div>

            <div className="brand-text">
              <span className="brand-title">{brand.name}</span>
              <span className="brand-subtitle">{brand.subtitle}</span>
            </div>
          </div>

          <button
            type="button"
            className="menu-toggle is-active mobile-menu__close"
            aria-label="Fechar menu"
            onClick={closeMenu}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <nav className="mobile-nav" aria-label="Menu mobile">
          {renderNavigation()}
        </nav>

        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          className="mobile-menu__cta"
          aria-label="Agendar atendimento pelo WhatsApp"
        >
          <Icons.Calendar className="mobile-menu__cta-icon" />
          <span>Agendar no WhatsApp</span>
        </a>
      </aside>
    </>
  );
}